import ListSearchBar from "../components/ListSearchBar";
/* eslint-disable eqeqeq */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  MapContainer,
  Marker,
  Popup,
  useMap,
  FeatureGroup
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "mapbox-gl/dist/mapbox-gl.css";
import "mapbox-gl-leaflet";
import L from "leaflet";

import { useTranslate } from "../hooks/LanguageProvider";
import IconMarker from "../components/Map/Icon";
import { Col, Row } from "react-bootstrap";
import "../components/Map/map.css";
import VehicleWidget from "../components/Map/vehicleWidget";
import AdvancedMapSearchDrawer, { AdvancedMapSearchValues } from "../components/Map/AdvancedMapSearchDrawer";
import MarkerClusterGroup from "react-leaflet-cluster";
import Slider from "react-slick";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import {
  Distance,
  engineStat,
  engineStatClass,
  parseServerUtcDate,
  toTimestamp,
} from "../utilities/functions";
import { useSpring, animated } from "react-spring";

import ItineraryLayerData from "../components/Map/ItineraryLayer";
import RouteSimulator from "../components/Map/RouteSimulator"; // ⬅️ simulateur réactivé
import { Bounce, toast } from "react-toastify";
import PrintControl from "../components/Map/PrintControl";

import FullscreenControlCustom from "../components/Map/FullscreenControlCustom";
import ZoomControl from "../components/ui/ZoomControl";
import { useParams } from "react-router-dom";
import BaseMapLayerControl from "../components/Map/BaseMapLayerControl";
import { useBaseMapPreference } from "../hooks/useBaseMapPreference";
import { usePagePreferences } from "../hooks/usePagePreferences";


const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

/* ------------------------------------------------------------
   Types
------------------------------------------------------------ */
type Vehicle = {
  id_vehicule: number;
  immatriculation_vehicule: string;
  LAT: number;
  LON: number;
  LNG?: number;
  SOG: number;
  COG: number;
  ENGINESTAT: number;
  TIMESTAMP: string;
  GPSDIST: number;
  PSN: string;
  nom_conducteur: string;
  nom_groupe: string;
  prenom_conducteur: string;
};

type UserOption = {
  id_user: number;
  username_user: string;
  nom_user?: string;
  prenom_user?: string;
  vehicles_count?: number;
};

type MapDisplayMode = "list" | "widget";
type MapPanelPosition = "left" | "right";

type toChartData = { x: number; y: number };

const MAP_SELECTED_CLIENT_STORAGE_KEY = "geotrackin:map:selected-client-user";

/* ------------------------------------------------------------
   Utils
------------------------------------------------------------ */
function ToUTCTime(isoDateString: string): number {
  if (!isoDateString) return 0;
  return parseServerUtcDate(isoDateString).getTime();
}

const pointTs = (p: any): number | null => {
  const raw = p?.end ?? p?.start ?? p?.TIMESTAMP_END ?? p?.TIMESTAMP ?? null;
  if (!raw) return null;
  const t = parseServerUtcDate(raw).getTime();
  return Number.isFinite(t) ? t : null;
};

const samePoint = (a: any, b: any) =>
  a && b &&
  a.LAT === b.LAT &&
  (a.LON ?? a.LNG) === (b.LON ?? b.LNG) &&
  pointTs(a) === pointTs(b);

const markerDataSignature = (data: Vehicle[]) => {
  let hash = 2166136261;
  const append = (value: unknown) => {
    const text = String(value ?? "");
    for (let index = 0; index < text.length; index += 1) {
      hash ^= text.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    hash ^= 124;
    hash = Math.imul(hash, 16777619);
  };

  [...data]
    .sort((a, b) => Number(a.id_vehicule) - Number(b.id_vehicule))
    .forEach((vehicle) => {
      [
      vehicle.id_vehicule,
      vehicle.PSN,
      vehicle.LAT,
      vehicle.LON ?? vehicle.LNG,
      vehicle.SOG,
      vehicle.ENGINESTAT,
      vehicle.TIMESTAMP,
      ].forEach(append);
    });

  return `${data.length}:${hash >>> 0}`;
};

const getUserDisplayName = (user?: UserOption) => {
  if (!user) return "";
  const fullName = `${user.nom_user || ""} ${user.prenom_user || ""}`.trim();
  return fullName || user.username_user || "";
};

/* ------------------------------------------------------------
   Hook: récupérer la map
------------------------------------------------------------ */
const MapEventHandler: React.FC<{ onMapReady: (mapInstance: any) => void }> = ({ onMapReady }) => {
  const map = useMap();
  useEffect(() => { if (map) onMapReady(map); }, [map, onMapReady]);
  return null;
};

/* ------------------------------------------------------------
   Composant principal
------------------------------------------------------------ */
const Map: React.FC = () => {
  const center: [number, number] = [30.5670197, 4.6911217];
  const zoom = 5;
  const MaxZoom = 18;
  const MinZoom = 5;
  const [printBounds, setPrintBounds] = useState<L.LatLngBoundsExpression | null>(null);
  const { translate } = useTranslate();
  const user_id = localStorage.getItem("userid");
  const isAdminMapAccount = Number(user_id) === 1;

  const { immatriculation_vehicule } = useParams();

  // --------- États data / UI ---------
  const [list_markers, setMarkers] = useState<Vehicle[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [advancedSearch, setAdvancedSearch] = useState<AdvancedMapSearchValues>({ psn: "", vehicle: "", group: "", driver: "", poi: "" });
  const [mapUserOptions, setMapUserOptions] = useState<UserOption[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [isClientSelectorOpen, setIsClientSelectorOpen] = useState(false);
  const [selectedMapUserId, setSelectedMapUserId] = useState<string>(() => {
    const stored = localStorage.getItem(MAP_SELECTED_CLIENT_STORAGE_KEY);
    return stored || localStorage.getItem("userid") || "";
  });
  const [baseMap, setBaseMap] = useBaseMapPreference("map");
  const {
    preferences: mapPreferences,
    setPreference: setMapPreference,
  } = usePagePreferences("map", { displayMode: "list", panelPosition: "left" });
  // The saved preference is the presentation mode. Bootstrap/CSS classes are
  // derived from it only for layout; they are not persisted as preferences.
  const mapDisplayMode: MapDisplayMode = mapPreferences.displayMode === "widget" ? "widget" : "list";
  const mapPanelPosition: MapPanelPosition = mapPreferences.panelPosition === "right" ? "right" : "left";
  const panelLayoutClass = mapDisplayMode === "widget" ? "layoutfloat" : "layoutleft";
  const canvasLayoutClass = mapDisplayMode === "widget" ? "col-12" : "col-9";
  const [vehicleDetail, setVehicleDetail] = useState<string>("none");
  const [selectedItem, setSelectedItem] = useState<Vehicle | null>(null);
  const [isMobileFleetListOpen, setIsMobileFleetListOpen] = useState(false);
  const [isMobileVehicleCardExpanded, setIsMobileVehicleCardExpanded] = useState(false);

  const [DistanceSpeedData, setDistanceSpeed] = useState<any>([]);
  const [TemperaturetData, setTemperature] = useState<any>([]);
  const [FuelTankData, setFuelTank] = useState<any>([]);

  // Itinéraire
  const [itineraryPoints, setItineraryPoints] = useState<any[]>([]);
  const [itineraryVisible, setItineraryVisible] = useState(false);
  const [activePSN, setActivePSN] = useState<string | null>(null);
  const [vehiclesVisible, setVehiclesVisible] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [itineraryNotice, setItineraryNotice] = useState<string | null>(null);
  const [followLive, setFollowLive] = useState<boolean>(true);
  const [autoFitItinerary, setAutoFitItinerary] = useState<boolean>(true);
  const lastTsRef = useRef<number | null>(null);

  // Graphiques / slider
  const [activeChart, setActiveChart] = useState<'distance' | 'fuel' | 'temp' | null>(null);
  const [chartPSN, setChartPSN] = useState<string | null>(null);

  // layout & filtres
  const [clicked, setClicked] = useState(false);
  const [slideState, setSlideState] = useState<string>("slide-state");
  const [ungroupMarker, setUngroupMarker] = useState<boolean>(true);
  const [Driving, setDriving] = useState(0);
  const [Parking, setParking] = useState(0);
  const [ParkingEngineRunning, setParkingEngineRunning] = useState(0);
  const [LastTransmission, setLastTransmission] = useState(0);
  const [vehicleState, setVehicleState] = useState<string | null>(null);

  const sliderRef = useRef<Slider | null>(null);
  const mapRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Animation UI liste
  const animationProps = useSpring({
    transform: clicked ? "scale(1.1)" : "scale(1)",
    overflow: mapDisplayMode === "list" ? "auto" : "hidden",
    marginTop: clicked ? "55px" : "10px",
  });

  // GroupComponent (cluster ou non)
  const GroupComponent: React.ComponentType<any> = ungroupMarker ? MarkerClusterGroup : FeatureGroup;
  const effectiveMapUserId = isAdminMapAccount ? (selectedMapUserId || user_id || "") : (user_id || "");
  const selectedMapUser = mapUserOptions.find((user) => String(user.id_user) === String(effectiveMapUserId));
  const filteredMapUserOptions = mapUserOptions.filter((user) => {
    const term = clientSearchTerm.trim().toLowerCase();
    if (!term) return true;
    return [
      getUserDisplayName(user),
      user.username_user,
      user.nom_user,
      user.prenom_user,
    ].some((value) => String(value || "").toLowerCase().includes(term));
  });

  // Slider charts
  const settings = { dots: false, infinite: true, speed: 500, slidesToShow: 1, slidesToScroll: 1, variableHeight: true };
  const goToSlide = (index: number) => sliderRef.current?.slickGoTo(index);

  const toolbarStyle: React.CSSProperties = {

    top: 10,
    zIndex: 1000,
    background: "#fff",
    padding: "4px 8px",
    borderRadius: 4,
    boxShadow: "0 1px 4px rgba(0,0,0,.3)",
    display: "flex",
    gap: 6,
    alignItems: "center",

  };

  // Divers
  const handleWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const container = containerRef.current;
    if (container) container.scrollTop += event.deltaY;
  };
  const mapLayoutClick = () => {
    setMapPreference("displayMode", mapDisplayMode === "widget" ? "list" : "widget");
  };
  const openAdvancedMapSearch = () => {
    if (advancedSearchOpen) {
      setAdvancedSearchOpen(false);
      return;
    }
    setSlideState("slide-state");
    setActiveChart(null);
    setChartPSN(null);
    setAdvancedSearchOpen(true);
  };
  const handleMarker = (state: string) => setVehicleState((curr) => (curr === state ? null : state));

  const timeZoneDST = (datetime: string) => toTimestamp(datetime);

  useEffect(() => {
    if (!isAdminMapAccount) {
      setSelectedMapUserId(user_id || "");
      return;
    }

    const stored = localStorage.getItem(MAP_SELECTED_CLIENT_STORAGE_KEY);
    setSelectedMapUserId(stored || user_id || "");
  }, [isAdminMapAccount, user_id]);

  useEffect(() => {
    if (!isAdminMapAccount || !user_id) return;

    let cancelled = false;

    const getMapUsers = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/users/map-clients/${user_id}`, {
          mode: "cors",
          credentials: "include",
        });
        if (!response.ok) return;

        const users = await response.json();
        if (!cancelled && Array.isArray(users)) {
          setMapUserOptions(users);
        }
      } catch (error) {
        console.error("Error fetching map users:", error);
      }
    };

    getMapUsers();

    return () => {
      cancelled = true;
    };
  }, [isAdminMapAccount, user_id]);

  const handleMapUserSelect = (nextUserId: number) => {
    const nextId = String(nextUserId);
    if (nextId === effectiveMapUserId) return;
    const nextUser = mapUserOptions.find((user) => String(user.id_user) === nextId);

    localStorage.setItem(MAP_SELECTED_CLIENT_STORAGE_KEY, nextId);
    setSelectedMapUserId(nextId);
    setClientSearchTerm(getUserDisplayName(nextUser));
    setSearchTerm("");
    setVehicleDetail("none");
    setSelectedItem(null);
    setClicked(false);
    setVehicleState(null);
    setItineraryVisible(false);
    setItineraryPoints([]);
    setActivePSN(null);
  };

  // ------------------------------------------------------------
  // API: getReportByType
  // ------------------------------------------------------------
  const getReportByType = async (type: number, PSN: string, slide: number) => {
    const res = await fetch(`${backendUrl}/api/map/reports/${type}/${PSN}`, { mode: "cors" });
    if (!res.ok) throw new Error("Erreur lors de la récupération du véhicule");
    if (slide != -1) goToSlide(slide);
    const data = await res.json();

    if (type == 1) {
      return data?.data ?? [];
    }

    if (type == 4) {
      setSlideState("");
      const arr = Array.isArray(data?.data) ? data.data.filter(Boolean) : [];
      if (!arr.length) {
        setDistanceSpeed([]);
        return [];
      }

      const firstGPSDIST = Number(arr[0]?.GPSDIST);
      let previousGPSDIST = Number.isFinite(firstGPSDIST) ? firstGPSDIST / 1000 : 0;
      let GPSDISTs = 0;

      const Distance: toChartData[] = arr.map((item: any) => {
        const rawGPSDIST = Number(item?.GPSDIST);
        const currentGPSDIST = Number.isFinite(rawGPSDIST) ? rawGPSDIST / 1000 : previousGPSDIST;
        const diff = currentGPSDIST - previousGPSDIST;
        previousGPSDIST = currentGPSDIST;
        GPSDISTs += diff;
        return { x: ToUTCTime(item.TIMESTAMP), y: Math.round(GPSDISTs) };
      });

      const Speed: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: Number.isFinite(Number(item?.SOG)) ? Number(item.SOG) : 0,
      }));
      const EngineStat: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: Number.isFinite(Number(item?.ENGINESTAT)) ? Number(item.ENGINESTAT) : 0,
      }));

      setDistanceSpeed({
        time: { timezoneOffset: -60 },
        chart: { zoomType: "x", height: 120, width: null },
        title: { text: "", align: "center" },
        subtitle: { text: "", align: "center" },
        xAxis: [{ gridLineWidth: 1, type: "datetime", crosshair: true }],
        yAxis: [
          {
            labels: { format: "{value} Km", style: { color: "#007bff" } },
            title: { text: translate("Distance"), style: { color: "#007bff" } },
            opposite: true,
          },
          {
            gridLineWidth: 0,
            title: { text: translate("Speed"), style: { color: "#41b346" } },
            labels: { format: "{value} Km/h", style: { color: "#41b346" } },
          },
          {
            gridLineWidth: 0,
            title: { text: translate("Contact"), style: { color: "#fd1e0d" } },
            labels: { format: "{value} ", style: { color: "#fd1e0d" } },
          },
        ],
        tooltip: { shared: true },
        legend: { layout: "horizontal", align: "center", x: 0, verticalAlign: "bottom", y: 21, floating: true },
        plotOptions: { series: { turboThreshold: Distance.length } },
        series: [
          { name: translate("Distance"), type: "spline", yAxis: 0, data: Distance, tooltip: { valueSuffix: " Km" } },
          { name: translate("Speed"), type: "spline", color: "#41b346", yAxis: 1, data: Speed, tooltip: { valueSuffix: " km/h" } },
          { name: translate("Contact"), type: "line", color: "#fd1e0d", yAxis: 2, data: EngineStat, tooltip: { valueSuffix: " " } },
        ],
      });
    }

    if (type == 8) {
      const arr = data.data;
      setSlideState("");

      const FIRST_FUELLVU = arr[0].FUELLVU;

      const TFUEL: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: parseFloat((item.FUELLVU - FIRST_FUELLVU).toFixed(2)),
      }));

      let previousVEHDIST = arr[0].VEHDIST / 1000;
      let VEHDISTs = 0;
      const VEHDIST: toChartData[] = arr.map((item: any) => {
        const current = item.VEHDIST / 1000;
        const diff = current - previousVEHDIST;
        previousVEHDIST = current;
        VEHDISTs += diff;
        return { x: ToUTCTime(item.TIMESTAMP), y: Math.round(VEHDISTs) };
      });

      const FUELLVL: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: Math.round(item.FUELLVL),
      }));

      setFuelTank({
        time: { timezoneOffset: -60 },
        chart: { zoomType: "x", height: 120 },
        title: { text: "", align: "center" },
        subtitle: { text: translate(""), align: "center" },
        xAxis: [{ gridLineWidth: 1, type: "datetime", crosshair: true }],
        yAxis: [
          {
            gridLineWidth: 0,
            title: { text: translate("Distance"), style: { color: "#007bff" } },
            labels: { format: "{value} Km", style: { color: "#007bff" } },
          },
          {
            labels: { format: "{value} %", style: { color: "#fd1e0d" } },
            title: { text: translate("Tank level"), style: { color: "#fd1e0d" } },
            opposite: true,
          },
          {
            gridLineWidth: 0,
            title: { text: translate("Fuel consumption"), style: { color: "#0080FF" } },
            labels: { format: "{value} L", style: { color: "#0080FF" } },
            opposite: true,
          },
        ],
        tooltip: { shared: true },
        legend: { layout: "horizontal", align: "center", x: 0, verticalAlign: "bottom", y: 21, floating: true },
        plotOptions: { series: { turboThreshold: VEHDIST.length } },
        series: [
          { name: translate("Distance"), type: "spline", yAxis: 0, data: VEHDIST, tooltip: { valueSuffix: " Km" } },
          { name: translate("Fuel consumption"), type: "spline", color: "#41b346", yAxis: 1, data: TFUEL, tooltip: { valueSuffix: " L" } },
          { name: translate("Tank level"), type: "line", color: "#fd1e0d", yAxis: 2, data: FUELLVL, tooltip: { valueSuffix: " %" } },
        ],
      });
    }

    if (type == 19) {
      const arr = data.data;
      setSlideState("");

      const AVGHUM: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: parseFloat(item.AVGHUM),
      }));
      const AVGTEMP: toChartData[] = arr.map((item: any) => ({
        x: ToUTCTime(item.TIMESTAMP),
        y: parseFloat(item.AVGTEMP) / 1000,
      }));

      setTemperature({
        time: { timezoneOffset: -60 },
        chart: { zoomType: "x", height: 120 },
        title: { text: translate(""), align: "center" },
        subtitle: { text: translate("Data values are averaged per period. Zoom to view more data"), align: "center" },
        xAxis: [{ gridLineWidth: 1, type: "datetime", crosshair: true }],
        yAxis: [
          {
            labels: { format: "{value} C°", style: { color: "#fd1e0d" } },
            title: { text: translate("Temperature"), style: { color: "#fd1e0d" } },
            opposite: true,
          },
          {
            gridLineWidth: 0,
            title: { text: translate("Humidity"), style: { color: "#0080FF" } },
            labels: { format: "{value} %", style: { color: "#0080FF" } },
          },
        ],
        tooltip: { shared: true },
        legend: { layout: "horizontal", align: "center", x: 0, verticalAlign: "bottom", y: 21, floating: true },
        plotOptions: { series: { turboThreshold: AVGTEMP.length } },
        series: [
          { name: translate("Temperature"), type: "spline", yAxis: 0, data: AVGTEMP, tooltip: { valueSuffix: " C°" } },
          { name: translate("Humidity"), type: "spline", color: "#41b346", yAxis: 1, data: AVGHUM, tooltip: { valueSuffix: " %" } },
        ],
      });
    }

    return null;
  };

  // ------------------------------------------------------------
  // Récupération des véhicules
  // ------------------------------------------------------------

  const isFirstLoadRef = useRef(true);
  const markersSignatureRef = useRef("");
  const BATCH_SIZE = 500;

  useEffect(() => {
    if (!effectiveMapUserId) return;

    let cancelled = false;
    let batchInterval: ReturnType<typeof setInterval> | null = null;
    isFirstLoadRef.current = true;
    markersSignatureRef.current = "";
    setMarkers([]);
    setSelectedItem(null);
    setVehicleDetail("none");

    const getMarkers = async () => {
      try {
        const response = await fetch(
          `${backendUrl}/api/map/find/${effectiveMapUserId}`,
          { mode: "cors" }
        );
        if (!response.ok) throw new Error("Failed to fetch vehicle data");

        const data: Vehicle[] = await response.json();
        if (cancelled) return;

        const nextSignature = markerDataSignature(data);
        if (nextSignature === markersSignatureRef.current) return;
        markersSignatureRef.current = nextSignature;

        // ==================================================
        // CALCULS MÉTIER (INCHANGÉS)
        // ==================================================
        const starting = data.filter(v => v.ENGINESTAT === 1 && v.SOG > 5);
        const parking = data.filter(v => v.ENGINESTAT === 0);
        const running = data.filter(v => v.ENGINESTAT === 1 && v.SOG < 5);
        const filtered = data.filter(v => {
          const hours =
            (Date.now() - parseServerUtcDate(v.TIMESTAMP).getTime()) / 3600000;
          return hours > 1;
        });

        setDriving(starting.length);
        setParking(parking.length);
        setParkingEngineRunning(running.length);
        setLastTransmission(filtered.length);

        // ==================================================
        // AFFICHAGE (SIMPLE & CLAIR)
        // ==================================================

        // 🔹 CAS 1 : PREMIER CHARGEMENT → DÉCOUPAGE
        if (isFirstLoadRef.current) {
          if (batchInterval) clearInterval(batchInterval);
          setMarkers(data.slice(0, BATCH_SIZE));

          let index = BATCH_SIZE;
          batchInterval = setInterval(() => {
            if (cancelled) {
              if (batchInterval) clearInterval(batchInterval);
              return;
            }

            setMarkers(prev => {
              const next = data.slice(index, index + BATCH_SIZE);
              index += BATCH_SIZE;

              if (!next.length) {
                if (batchInterval) clearInterval(batchInterval);
                isFirstLoadRef.current = false; // 🔑 fini le premier chargement
                return prev;
              }

              return prev.concat(next);
            });
          }, 200);

          return;
        }

        // 🔹 CAS 2 : AUTRES CHARGEMENTS → DIRECT
        if (!cancelled) setMarkers(data);

      } catch (err) {
        console.error("Error fetching vehicle data:", err);
      }
    };

    getMarkers();
    const refreshInterval = setInterval(getMarkers, 6000);
    return () => {
      cancelled = true;
      clearInterval(refreshInterval);
      if (batchInterval) clearInterval(batchInterval);
    };
  }, [effectiveMapUserId]);


 const initialUrlFocusDoneRef = useRef(false);

useEffect(() => {
  const map = mapRef.current;
  if (!map) return;

  if (!immatriculation_vehicule) return;
  if (!list_markers?.length) return;
  if (initialUrlFocusDoneRef.current) return;

  const normalize = (s: string) =>
    s.replace(/[-\s]/g, "").toLowerCase();

  const vehicle = list_markers.find((v: any) =>
    normalize(v.immatriculation_vehicule) === normalize(immatriculation_vehicule)
  );

  if (vehicle) {
    map.flyTo([vehicle.LAT, vehicle.LON], 15, { duration: 1 });
    initialUrlFocusDoneRef.current = true;
  }
}, [immatriculation_vehicule, list_markers]);
  // ------------------------------------------------------------
  // Sélection véhicule / recentrage
  // ------------------------------------------------------------
const flyToVehicle = useCallback((map: any, vehicle: Vehicle, zoomLevel?: number) => {
  if (!vehicle || !map) return;

  const currentZoom = map.getZoom();
  const finalZoom = zoomLevel ?? Math.max(currentZoom, 13);

  map.flyTo([vehicle.LAT, vehicle.LON], finalZoom, { duration: 1 });
}, []);

  const getSelectedMarkerPulseColor = (vehicle: Vehicle) => {
    const state = engineStatClass(vehicle.ENGINESTAT, vehicle.SOG);
    if (state === "move") return "#22c55e";
    if (state === "stop") return "#0d6efd";
    if (state === "pause") return "#f59e0b";
    return "#64748b";
  };

  useEffect(() => {
    const map = mapRef.current;
    if (selectedItem && map) flyToVehicle(map, selectedItem);
  }, [selectedItem, flyToVehicle]);

  const handleItemClick = (item: Vehicle) => {
    if (!mapRef.current) return;
    setVehicleDetail("flex");
    setSearchTerm(item.immatriculation_vehicule);
    setSelectedItem(item);
   flyToVehicle(mapRef.current, item, 13);
    setClicked(true);
    setIsMobileFleetListOpen(false);
    setIsMobileVehicleCardExpanded(false);
  };

  // ------------------------------------------------------------
  // Handlers sliders (TOGGLE)
  // ------------------------------------------------------------
  const handleOnCloseWidget = () => {
    setVehicleDetail("none");
    setSearchTerm("");
    setSlideState("slide-state");
    setClicked(false);
  };

  const handleDistanceSpeedClick = (PSN: string) => {
    if (activeChart === 'distance' && chartPSN === PSN && slideState === '') {
      setSlideState('slide-state'); setActiveChart(null); setChartPSN(null); return;
    }
    setActiveChart('distance'); setChartPSN(PSN); setSlideState('');
    getReportByType(4, PSN, 0); if (typeof goToSlide === 'function') goToSlide(0);
  };

  const handleFuelTankClick = (PSN: string) => {
    if (activeChart === 'fuel' && chartPSN === PSN && slideState === '') {
      setSlideState('slide-state'); setActiveChart(null); setChartPSN(null); return;
    }
    setActiveChart('fuel'); setChartPSN(PSN); setSlideState('');
    getReportByType(8, PSN, 1); if (typeof goToSlide === 'function') goToSlide(1);
  };

  const handleTemperatureDiagramClick = (PSN: string) => {
    if (activeChart === 'temp' && chartPSN === PSN && slideState === '') {
      setSlideState('slide-state'); setActiveChart(null); setChartPSN(null); return;
    }
    setActiveChart('temp'); setChartPSN(PSN); setSlideState('');
    getReportByType(19, PSN, 2); if (typeof goToSlide === 'function') goToSlide(2);
  };

  // ------------------------------------------------------------
  // Itinéraire : toggle + vide + live append 15s
  // ------------------------------------------------------------
  const handleItineraryClick = useCallback(async (PSN: string) => {
    // Toggle OFF si déjà en R.I. sur ce PSN
    if (itineraryVisible && activePSN === PSN) {
      setItineraryVisible(false);
      setItineraryPoints([]);
      setActivePSN(null);
      setVehiclesVisible(true);
      setItineraryNotice(null);
      lastTsRef.current = null;

      toast.info(translate("Itinerary hidden"), {
        position: "bottom-right",
        autoClose: 2400,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      return;
    }

    // Toast de chargement
    const loadingId = toast.loading(translate("Loading itinerary…"), {
      position: "bottom-right",
      // on laisse sans autoClose pour un vrai “loading”
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      theme: "light",
      transition: Bounce,
    });

    try {
      setLoading(true);
      setErr(null);
      setItineraryNotice(null);
      setActivePSN(PSN);

      const raw = await getReportByType(1, PSN, -1);
      const points = Array.isArray(raw) ? raw : (raw?.repportDatas ?? raw?.data ?? []);

      // Cas vide → toast.warn
      if (!points || points.length === 0) {
        setItineraryVisible(false);
        setItineraryPoints([]);
        setActivePSN(null);
        setVehiclesVisible(true);
        setItineraryNotice(null);
        lastTsRef.current = null;

        toast.update(loadingId, {
          render: translate("No itinerary data for this vehicle / period."),
          type: "warning",
          isLoading: false,
          autoClose: 2400,
          closeOnClick: true,
          pauseOnHover: true,
        });
        return;
      }

      // OK → afficher tracé + toast success
      setItineraryPoints(points);
      setItineraryVisible(true);
      setVehiclesVisible(false);
      setAutoFitItinerary(true);
      setVehicleDetail("none");
      setSearchTerm("");
      setSelectedItem(null);
      setIsMobileFleetListOpen(false);
      setIsMobileVehicleCardExpanded(false);
      setClicked(false);
      lastTsRef.current = pointTs(points[points.length - 1]);

      toast.update(loadingId, {
        render: translate("Itinerary loaded"),
        type: "success",
        isLoading: false,
        autoClose: 1600,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } catch (e: any) {
      // Erreur → toast.error
      setErr(e?.message ?? translate("Error while loading itinerary"));
      setItineraryPoints([]);
      setItineraryVisible(false);
      setVehiclesVisible(true);
      setItineraryNotice(null);
      lastTsRef.current = null;

      toast.update(loadingId, {
        render: e?.message ?? translate("Error while loading itinerary"),
        type: "error",
        isLoading: false,
        autoClose: 3200,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } finally {
      setLoading(false);
    }
  }, [itineraryVisible, activePSN, translate]);

  // Désactiver l'autoFit après 1er rendu
  useEffect(() => {
    if (itineraryVisible && autoFitItinerary && itineraryPoints.length) {
      const t = setTimeout(() => setAutoFitItinerary(false), 0);
      return () => clearTimeout(t);
    }
  }, [itineraryVisible, autoFitItinerary, itineraryPoints.length]);

  // Live append 15s
  useEffect(() => {
    if (!itineraryVisible || !activePSN) return;

    const id = setInterval(async () => {
      try {
        const raw = await getReportByType(1, activePSN, -1);
        const incoming = Array.isArray(raw) ? raw : (raw?.repportDatas ?? raw?.data ?? []);
        if (!incoming || incoming.length === 0) return;

        const apiLast = pointTs(incoming[incoming.length - 1]);

        if (lastTsRef.current == null) {
          setItineraryPoints(incoming);
          lastTsRef.current = apiLast;
          return;
        }

        const news = incoming.filter((p: any) => {
          const ts = pointTs(p);
          return ts !== null && ts > (lastTsRef.current as number);
        });

        if (news.length) {
          setItineraryPoints((prev) => {
            const merged = [...prev, ...news];
            if (merged.length >= 2 && samePoint(merged[merged.length - 1], merged[merged.length - 2])) {
              merged.splice(merged.length - 1, 1);
            }
            return merged;
          });
          lastTsRef.current = apiLast ?? lastTsRef.current;

          if (followLive && mapRef.current) {
            const last = news[news.length - 1];
            const lng = (last.LON ?? last.LNG);
            if (typeof lng === "number") mapRef.current.panTo([last.LAT, lng]);
          }
        }
      } catch {
        // silencieux
      }
    }, 15000);

    return () => clearInterval(id);
  }, [itineraryVisible, activePSN, followLive]);

  // ------------------------------------------------------------
  // Filtres / liste
  // ------------------------------------------------------------
  const filteredItems = list_markers.filter(item => {
    const term = searchTerm.trim().replace(/\s+/g, " ").toLowerCase();
    const matchesSimple = [item.immatriculation_vehicule,
      [item.nom_conducteur, item.prenom_conducteur].filter(Boolean).join(" "),
      [item.prenom_conducteur, item.nom_conducteur].filter(Boolean).join(" ")
    ].some(value => (value || "").toLowerCase().includes(term));
    const matchesAdvanced = [
      [item.PSN, advancedSearch.psn],
      [item.immatriculation_vehicule, advancedSearch.vehicle],
      [item.nom_groupe, advancedSearch.group],
      [[item.nom_conducteur, item.prenom_conducteur].filter(Boolean).join(" "), advancedSearch.driver],
    ].every(([value, filter]) => !filter.trim() || filter.split("||").some((term) => (value || "").toLowerCase().includes(term.trim().toLowerCase())));
    return matchesSimple && matchesAdvanced;
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setVehicleDetail("none");
  };

  // Path pour simulateur
  const simPath = useMemo<[number, number][]>(() =>
    (itineraryPoints ?? [])
      .filter((p: any) => typeof p?.LAT === "number" && (typeof p?.LON === "number" || typeof p?.LNG === "number"))
      .map((p: any) => [p.LAT, (p.LON ?? p.LNG) as number])
    , [itineraryPoints]);

  const simulatorPointSpeeds = useMemo<number[]>(() =>
    (itineraryPoints ?? [])
      .filter((p: any) => typeof p?.LAT === "number" && (typeof p?.LON === "number" || typeof p?.LNG === "number"))
      .map((point: any) => {
        const speedValue = Number(point?.SOG ?? point?.speed ?? point?.Speed ?? point?.speed_position ?? 0);
        return Number.isFinite(speedValue) ? speedValue : 0;
      })
    , [itineraryPoints]);

  const simulatorInitialSpeed = useMemo(() => {
    const pointSpeeds = (itineraryPoints ?? [])
      .map((point: any) => Number(point?.SOG ?? point?.speed ?? point?.Speed ?? 0))
      .filter((speedValue: number) => Number.isFinite(speedValue) && speedValue > 0);

    if (pointSpeeds.length > 0) {
      const averageSpeed =
        pointSpeeds.reduce((sum: number, speedValue: number) => sum + speedValue, 0) /
        pointSpeeds.length;
      return Math.min(200, Math.max(10, Math.round(averageSpeed)));
    }

    const activeVehicle = list_markers.find((vehicle) => vehicle.PSN === activePSN);
    const activeVehicleSpeed = Number(activeVehicle?.SOG ?? 0);
    if (Number.isFinite(activeVehicleSpeed) && activeVehicleSpeed > 0) {
      return Math.min(200, Math.max(10, Math.round(activeVehicleSpeed)));
    }

    return 50;
  }, [activePSN, itineraryPoints, list_markers]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  const [isPrinting, setIsPrinting] = useState(false);

  const periodLabel = /* ex: "01/09/2025 08:00 → 01/09/2025 18:00" */ "";
  const onBeforePrint = useCallback(() => {
    document.body.classList.add("print-map");
    setIsPrinting(true);
  }, []);
  const onAfterPrint = useCallback(() => {
    setIsPrinting(false);
    document.body.classList.remove("print-map");
  }, []);

  const printHeaderText = useMemo(() => {
    const now = new Date().toLocaleString();
    return `R.I. ${activePSN ? `— PSN: ${activePSN} ` : ""}${periodLabel ? `— ${periodLabel} ` : ""}— ${now}`;
  }, [activePSN, periodLabel]);






  return (
    <>
      <Row className={`fleet-map-page fleet-map-page--${mapDisplayMode} fleet-map-page--panel-${mapPanelPosition}`} data-map-display-mode={mapDisplayMode} data-map-panel-position={mapPanelPosition}>
        {/* Liste véhicules */}
        <div className={`${panelLayoutClass} col-12 col-md-3 fleet-map-panel fleet-map-panel--${mapDisplayMode} fleet-map-panel--position-${mapPanelPosition} ${isMobileFleetListOpen ? "is-open" : ""} ${itineraryVisible && !isMobileFleetListOpen ? "is-itinerary-hidden" : ""}`} style={{ zIndex: 9999, paddingLeft: "30px", margin: " 20px 0" }}>
          <div className="fleet-map-mobile-panel-header">
            <strong>{translate("Vehicles")}</strong>
            <button
              type="button"
              className="fleet-map-mobile-panel-close"
              onClick={() => setIsMobileFleetListOpen(false)}
              aria-label={translate("Close")}
            >
              <i className="las la-times" />
            </button>
          </div>
          <div className="fleet-map-statusbar" style={{ margin: "0px 0 15px 0" }}>
            <button
              type="button"
              className={`btn btn-default btn-sm pull-left act ${vehicleState !== "move" ? "active" : ""}`}
              id="act_moving" onClick={() => handleMarker("move")}
              title={translate("Engine running")}
            >
              <span>{Driving} </span>
              <i className="la la-play-circle" style={{ color: "green", fontSize: "20px" }} />
            </button>
            <button
              type="button"
              className={`btn btn-delault btn-sm pull-left act ${vehicleState !== "pause" ? "active" : ""}`}
              id="act_pause" onClick={() => handleMarker("pause")}
              title={translate("Parking with engine started")}
            >
              <span>{ParkingEngineRunning} </span>
              <i className="la la-pause-circle" style={{ color: "red", fontSize: "20px" }} />
            </button>
            <button
              type="button"
              className={`btn btn-delault btn-sm pull-left act ${vehicleState !== "stop" ? "active" : ""}`}
              id="act_stoping" onClick={() => handleMarker("stop")}
              title={translate("Parking")}
            >
              <span>{Parking} </span>
              <i className="las la-stop-circle" style={{ color: "blue", fontSize: "20px" }} />
            </button>
            <button
              type="button"
              className={`btn btn-delault btn-sm pull-left act ${vehicleState !== "absence" ? "active" : ""}`}
              id="act_absence" onClick={() => handleMarker("absence")}
              title={translate("Last transmission")}
            >
              <span>{LastTransmission} </span>
              <i className="la la-history" style={{ fontSize: "20px" }} />
            </button>
          </div>

          <div className="map-search fleet-map-search">
            <ListSearchBar onReset={() => {
              setSearchTerm(""); setVehicleDetail("none"); setSelectedItem(null);
              setIsMobileVehicleCardExpanded(false); setClicked(false);
            }} resetLabel={translate("Reset search")}>
              <input type="text" placeholder={translate("Search vehicle, driver...")}
                value={searchTerm} onChange={handleSearchChange} />
            </ListSearchBar>
            <span className="fleet-map-search-count">({list_markers.length})</span>
            <button type="button" className={`fleet-map-advanced-search-button ${advancedSearchOpen ? "active" : ""}`} onClick={openAdvancedMapSearch} title="Recherche avancée" aria-label="Recherche avancée" aria-expanded={advancedSearchOpen}>
              <i className="las la-filter" />
            </button>
          </div>

          <animated.div
            className="map-list"
            ref={containerRef}
            onWheel={handleWheelScroll}
            tabIndex={0}
            style={animationProps}
          >
            {filteredItems.map((item) => {
              // flags actifs pour CE véhicule (pour styler les 4 boutons)
              const isRIActive = itineraryVisible && activePSN === item.PSN;
              const isDistanceActive = activeChart === 'distance' && chartPSN === item.PSN && slideState === '';
              const isFuelActive = activeChart === 'fuel' && chartPSN === item.PSN && slideState === '';
              const isTempActive = activeChart === 'temp' && chartPSN === item.PSN && slideState === '';

              return (
                (vehicleState === null || vehicleState === engineStatClass(item.ENGINESTAT, item.SOG)) && (
                  <div
                    key={item.id_vehicule}
                    id={item.id_vehicule.toString()}
                    onClick={(e) => { e.stopPropagation(); handleItemClick(item); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleItemClick(item); } }}
                    className={`${mapDisplayMode === "widget" ? "VehicleWidget" : "fleet-map-list-item"} ${engineStatClass(item.ENGINESTAT, item.SOG)}`}
                  >
                    {mapDisplayMode === "list" ? (
                      <div className="fleet-map-list-row" role="button" tabIndex={0}>
                        <span className="fleet-map-mobile-vehicle-state">
                          <i className="las la-shield-alt" aria-hidden="true" />
                          <img src={engineStat(item.ENGINESTAT, item.SOG, translate).iconState} alt="" />
                        </span>
                        <span className="fleet-map-mobile-vehicle-main">
                          <strong>{item.immatriculation_vehicule}</strong>
                        </span>
                        <span className="fleet-map-list-row-actions">
                          <button type="button" title={translate("Distance report")} aria-label={translate("Distance report")} onClick={(event) => { event.stopPropagation(); handleDistanceSpeedClick(item.PSN); }}>
                            <i className="las la-chart-line" />
                          </button>
                          <button type="button" title={translate("Itinerary report")} aria-label={translate("Itinerary report")} onClick={(event) => { event.stopPropagation(); handleItineraryClick(item.PSN); }}>
                            <i className="las la-road" />
                          </button>
                        </span>
                      </div>
                    ) : (
                      <VehicleWidget
                        id={item.id_vehicule}
                        matriculation={item.immatriculation_vehicule}
                        timestamp={timeZoneDST(item.TIMESTAMP)}
                        SOG={item.SOG}
                        LAT={item.LAT}
                        LON={item.LON}
                        COG={item.COG}
                        enginestat={item.ENGINESTAT}
                        gpsdist={item.GPSDIST}
                        detail={vehicleDetail}
                        PSN={item.PSN}
                        nom_conducteur={item.nom_conducteur}
                        nom_groupe={item.nom_groupe}
                        prenom_conducteur={item.prenom_conducteur}
                        handleItineraryClick={handleItineraryClick}
                        handleDistanceSpeedClick={handleDistanceSpeedClick}
                        handleFuelTankClick={handleFuelTankClick}
                        handleTemperatureDiagramClick={handleTemperatureDiagramClick}
                        onClose={handleOnCloseWidget}
                        isRIActive={isRIActive}
                        isDistanceActive={isDistanceActive}
                        isFuelActive={isFuelActive}
                        isTempActive={isTempActive}
                      />
                    )}
                  </div>
                )
              );
            })}
          </animated.div>
        </div>

        {/* Carte */}
        <div className={`${canvasLayoutClass} fleet-map-canvas fleet-map-canvas--${mapDisplayMode} fleet-map-canvas--position-${mapPanelPosition}`} style={{ padding: 0, position: "relative" }}>
          {!itineraryVisible && (
            <div className="fleet-map-map-controls" aria-label={translate("Map controls")}>
              <button
                type="button"
                className="fleet-map-map-control fleet-map-map-control--group"
                onClick={() => setUngroupMarker((v) => !v)}
                title={translate("Group or ungroup vehicles")}
                aria-label={translate("Group or ungroup vehicles")}
              >
                <i className="la la-object-ungroup" />
              </button>
              <button
                type="button"
                className={`fleet-map-map-control fleet-map-map-control--list ${mapDisplayMode === "list" ? "active" : ""}`}
                onClick={mapLayoutClick}
                aria-label={translate("Toggle vehicle list")}
                title={translate("Toggle vehicle list")}
              >
                <i className="las la-list" />
              </button>
            </div>
          )}
          <AdvancedMapSearchDrawer
            show={advancedSearchOpen}
            onHide={() => setAdvancedSearchOpen(false)}
            values={advancedSearch}
            userId={effectiveMapUserId}
            onApply={(values) => { setAdvancedSearch(values); setSearchTerm(""); }}
            onReset={() => { setAdvancedSearch({ psn: "", vehicle: "", group: "", driver: "", poi: "" }); setSearchTerm(""); }}
          />

          {!isMobileFleetListOpen && (
            <button
              type="button"
              className="fleet-map-mobile-search-button"
              onClick={() => {
                setClicked(false);
                setIsMobileFleetListOpen(true);
              }}
              aria-label={translate("Search")}
            >
              <i className="las la-search" />
            </button>
          )}

          {selectedItem && (
            <div
              className={`fleet-map-selected-card ${isMobileVehicleCardExpanded ? "is-expanded" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => setIsMobileVehicleCardExpanded((current) => !current)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIsMobileVehicleCardExpanded((current) => !current);
                }
              }}
            >
              <div className="fleet-map-selected-card__header">
                <div>
                  <strong>{selectedItem.immatriculation_vehicule}</strong>
                  <small>{timeZoneDST(selectedItem.TIMESTAMP)}</small>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedItem(null);
                    setIsMobileVehicleCardExpanded(false);
                  }}
                  aria-label={translate("Close")}
                >
                  <i className="las la-times" />
                </button>
              </div>
              <div className="fleet-map-selected-card__grid">
                <span>
                  <i className="las la-map-marker" />
                  {selectedItem.LAT.toFixed(6)}, {selectedItem.LON.toFixed(6)}
                </span>
                <span>
                  <i className="las la-tachometer-alt" />
                  {selectedItem.SOG} km/h
                </span>
                <span>
                  <i className="las la-road" />
                  {Distance(selectedItem.GPSDIST)}
                </span>
              </div>

              <div className="fleet-map-selected-card__details">
                <div>
                  <small>{translate("Group")}</small>
                  <strong>{selectedItem.nom_groupe || translate("None")}</strong>
                </div>
                <div>
                  <small>{translate("Driver")}</small>
                  <strong>
                    {selectedItem.nom_conducteur
                      ? `${selectedItem.nom_conducteur} ${selectedItem.prenom_conducteur || ""}`
                      : translate("None")}
                  </strong>
                </div>
                <div>
                  <small>{translate("Position")}</small>
                  <strong>{selectedItem.LAT.toFixed(6)}, {selectedItem.LON.toFixed(6)}</strong>
                </div>
              </div>

              <div className="fleet-map-selected-card__actions">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleItineraryClick(selectedItem.PSN);
                  }}
                >
                  <i className="las la-route" />
                  <span>R.I.</span>
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDistanceSpeedClick(selectedItem.PSN);
                  }}
                >
                  <i className="las la-chart-line" />
                  <span>{translate("Distance")}</span>
                </button>
              </div>
            </div>
          )}


          {/* Boutons R.I. */}
          {itineraryVisible && (
            <div className="ri-toolbar">
              {/* Toggle Follow */}
              <button
                type="button"
                style={{
                  ...toolbarStyle,
                  opacity: 0.9,
                  background: '#fff', // facultatif si tu veux voir l'opacité
                  padding: '7px',
                  borderRadius: '8px',
                  border: "1px solid #ddd"
                }}
                className={`icon-btn ${followLive ? "is-on" : "is-off"}`}
                onClick={() => setFollowLive(v => !v)}
                title={followLive ? translate("Stop following") : translate("Follow in real time")}
                aria-pressed={followLive}
              >
                {/* icône ciblage */}
                <i className={`las la-crosshairs ${followLive ? "orange" : "grey"}`} style={{ fontSize: "22px" }} />
                {followLive ? translate("Stop following") : translate("Follow in real time")}
              </button>

              {/* Quitter R.I. */}
              <button
                type="button"
                className="icon-btn danger"
                style={{
                  ...toolbarStyle,
                  opacity: 0.9,
                  background: '#fff', // facultatif si tu veux voir l'opacité
                  padding: '7px',
                  borderRadius: '8px',
                  border: "1px solid #ddd"
                }}
                onClick={() => {
                  const activeVehicle = list_markers.find((vehicle) => vehicle.PSN === activePSN);
                  setItineraryVisible(false);
                  setItineraryPoints([]);
                  setActivePSN(null);
                  setVehiclesVisible(true);
                  setItineraryNotice(null);
                  setVehicleDetail(activeVehicle ? "flex" : "none");
                  setSearchTerm(activeVehicle?.immatriculation_vehicule ?? "");
                  setSelectedItem(activeVehicle ?? null);
                  setIsMobileFleetListOpen(false);
                  setIsMobileVehicleCardExpanded(false);
                  setClicked(!!activeVehicle);
                  lastTsRef.current = null;
                  if (activeVehicle && mapRef.current) {
                    flyToVehicle(mapRef.current, activeVehicle, 15);
                  }
                }}
                title={translate("Exit Itinerary reconstitution")}
              >
                {/* icône sortie */}
                <i className="las la-sign-out-alt" style={{ fontSize: 22 }} /> Exit R.I.
              </button>
            </div>
          )}

          {isAdminMapAccount && (
            <div className="fleet-map-client-selector fleet-map-client-selector--map">
              <button
                type="button"
                className="fleet-map-client-selector__toggle"
                onClick={() => {
                  setClientSearchTerm(getUserDisplayName(selectedMapUser));
                  setIsClientSelectorOpen((current) => !current);
                }}
                title={getUserDisplayName(selectedMapUser) || translate("Client")}
                aria-label={translate("Client")}
              >
                <i className="las la-user" />
              </button>
              {isClientSelectorOpen && (
                <div className="fleet-map-client-selector__popover">
                  <div className="fleet-map-client-selector__search">
                    <i className="las la-search" />
                    <input
                      type="text"
                      value={clientSearchTerm}
                      onChange={(event) => setClientSearchTerm(event.target.value)}
                      placeholder={translate("Search user")}
                    />
                    {clientSearchTerm && (
                      <button
                        type="button"
                        onClick={() => setClientSearchTerm("")}
                        aria-label={translate("Reset")}
                      >
                        <i className="las la-times" />
                      </button>
                    )}
                  </div>
                  <div className="fleet-map-client-selector__list">
                    {filteredMapUserOptions.slice(0, 30).map((user) => (
                      <button
                        type="button"
                        key={user.id_user}
                        className={String(user.id_user) === String(effectiveMapUserId) ? "is-active" : ""}
                        onClick={() => {
                          setClientSearchTerm(getUserDisplayName(user));
                          handleMapUserSelect(user.id_user);
                          setIsClientSelectorOpen(false);
                        }}
                      >
                        <span>{getUserDisplayName(user)}</span>
                        <small>
                          <i className="las la-car" />
                          {Number(user.vehicles_count || 0)}
                        </small>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <MapContainer
            center={center}
            zoom={zoom}
            minZoom={MinZoom}
            maxZoom={MaxZoom}
            zoomControl={false}
            style={{
              padding: 0,
              margin: 0,
              height: "86vh",
              width: "100%",
              zIndex: 1,
            }}
          >
            <MapEventHandler
              onMapReady={(mapInstance) => {
                mapRef.current = mapInstance;
              }}
            />

            <BaseMapLayerControl
              value={baseMap}
              onChange={setBaseMap}
              tokenUrl={`${backendUrl}/api/mapkit/token`}
              top={132}
            />

            <ZoomControl position="topright" />
            <FullscreenControlCustom position="topright" />

            {vehiclesVisible && (
              <GroupComponent>
                {filteredItems.map((item) => (
                  <Marker
                    key={item.id_vehicule}
                    position={[item.LAT, item.LON]}
                    icon={IconMarker(
                      item.COG,
                      "asset/images/mapicon/car.svg",
                      () => handleItemClick(item),
                      selectedItem?.id_vehicule === item.id_vehicule,
                      getSelectedMarkerPulseColor(item)
                    )}
                  >
                    <Popup>
                      <span onClick={() => handleItemClick(item)}>
                        <img
                          src={engineStat(item.ENGINESTAT, item.COG, translate).iconState}
                          alt=""
                        />{" "}
                        {item.immatriculation_vehicule}
                      </span>
                    </Popup>
                  </Marker>
                ))}
              </GroupComponent>
            )}

            <ItineraryLayerData
              points={itineraryPoints}
              visible={itineraryVisible}
              color="#0ea5e9"
              autoFit={autoFitItinerary}
            />

            {itineraryVisible && simPath.length >= 2 && (
              <RouteSimulator
                path={simPath}
                pointSpeeds={simulatorPointSpeeds}
                speed={simulatorInitialSpeed}
                autofit={false}
                right={true}
              />
            )}

            <PrintControl
              position="topright"
              orientation="landscape"
              pageSize="A4"
              fitOnPrint="selection"
              selectionEnabled={true}
            />
          </MapContainer>

        </div>

        {/* Slider charts */}
        <div
          className="layoutfloat"
          style={{
            zIndex: 9999,
            background: " #fff",
            bottom: "0px",
            border: " 1px solid #ddd; ",
            padding: "0",
          }}
        >
          <Slider ref={sliderRef} {...settings} className={`${slideState}`}>
            {/* Distance */}
            <div className="dvc">
              <i
                className="las la-times"
                style={{ position: "absolute", zIndex: 9999, border: "1px solid #ddd", borderRadius: "19px", padding: "6px" }}
                onClick={() => { setSlideState("slide-state"); setActiveChart(null); setChartPSN(null); }}
              />
              <Row>
                <Col xs={12}>
                  <h5 style={{ textAlign: "center" }}>
                    {translate("Diagram of distance, speed and contact")}
                  </h5>
                  {DistanceSpeedData && Object.keys(DistanceSpeedData).length > 0 ? (
                    <div style={{ backgroundColor: "#fff", padding: "0", margin: "8" }}>
                      <HighchartsReact highcharts={Highcharts} options={DistanceSpeedData} />
                    </div>
                  ) : (
                    <div style={{ backgroundColor: "#fff", padding: "16px", margin: "8px", textAlign: "center" }}>
                      {translate("No data to display No data to display for distance, speed and contact")}
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            {/* Fuel */}
            <div className="dvc">
              <i
                className="las la-times"
                style={{ position: "absolute", zIndex: 9999, border: "1px solid #ddd", borderRadius: "19px", padding: "6px" }}
                onClick={() => { setSlideState("slide-state"); setActiveChart(null); setChartPSN(null); }}
              />
              <Row>
                <h5 style={{ textAlign: "center" }}>{translate("Fuel Tank Diagram")}</h5>
                <Col xs={12}>
                  {FuelTankData && Object.keys(FuelTankData).length > 0 ? (
                    <div style={{ backgroundColor: "#fff", padding: "0", margin: "8" }}>
                      <HighchartsReact highcharts={Highcharts} options={FuelTankData} />
                    </div>
                  ) : (
                    <div style={{ backgroundColor: "#fff", padding: "16px", margin: "8px", textAlign: "center" }}>
                      {translate("No data to display for fuel tank graph")}
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            {/* Temp */}
            <div className="dvc">
              <i
                className="las la-times"
                style={{ position: "absolute", zIndex: 9999, border: "1px solid #ddd", borderRadius: "19px", padding: "6px" }}
                onClick={() => { setSlideState("slide-state"); setActiveChart(null); setChartPSN(null); }}
              />
              <Row>
                <Col xs={12}>
                  <h5 style={{ textAlign: "center" }}>{translate("Temperature Diagram")}</h5>
                  {TemperaturetData && Object.keys(TemperaturetData).length > 0 ? (
                    <div style={{ backgroundColor: "#fff", padding: "0", margin: "8" }}>
                      <HighchartsReact highcharts={Highcharts} options={TemperaturetData} />
                    </div>
                  ) : (
                    <div style={{ backgroundColor: "#fff", padding: "16px", margin: "8px", textAlign: "center" }}>
                      {translate("No data to display for temperature graph")}
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </Slider>
        </div>
      </Row>
    </>
  );
};

export default Map;
